import './RouteResults.css';

export interface ScoreBreakdown {
  departureStaffed: boolean;
  arrivalStaffed: boolean;
  enrouteCoveragePercent: number;
  ctrCoveragePercent: number;
  longestGapNm: number;
  continuityBonus: number;
}

export interface ScoredRoute {
  departure: string;
  arrival: string;
  distance: number;
  estimatedBlockTime: number;
  coverageScore: number;
  scoreBreakdown: ScoreBreakdown;
  explanation: string;
  routeString: string;
  departureLat: number;
  departureLon: number;
  arrivalLat: number;
  arrivalLon: number;
  departureAirportName?: string;
  arrivalAirportName?: string;
}

interface RouteResultsProps {
  routes: ScoredRoute[];
  onViewMap: (route: ScoredRoute) => void;
  onPlanInSimBrief: (route: ScoredRoute) => void;
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'score-high';
  if (score >= 50) return 'score-medium';
  return 'score-low';
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 50) return 'Good';
  return 'Limited';
}

export default function RouteResults({ routes, onViewMap, onPlanInSimBrief }: RouteResultsProps) {
  if (routes.length === 0) {
    return (
      <div className="route-results empty">
        <p>No routes found matching your criteria. Try adjusting your search parameters.</p>
      </div>
    );
  }

  return (
    <div className="route-results">
      <h2>Recommended Routes ({routes.length})</h2>
      <div className="routes-list">
        {routes.map((route, index) => (
          <div key={`${route.departure}-${route.arrival}-${index}`} className="route-card">
            <div className="route-header">
              <div className="route-airports">
                <div className="airport">
                  <span className="airport-code">{route.departure}</span>
                  {route.departureAirportName && (
                    <span className="airport-name">{route.departureAirportName}</span>
                  )}
                </div>
                <div className="route-arrow">→</div>
                <div className="airport">
                  <span className="airport-code">{route.arrival}</span>
                  {route.arrivalAirportName && (
                    <span className="airport-name">{route.arrivalAirportName}</span>
                  )}
                </div>
              </div>
              <div className={`score-badge ${getScoreColor(route.coverageScore)}`}>
                <span className="score-value">{route.coverageScore}</span>
                <span className="score-label">{getScoreLabel(route.coverageScore)}</span>
              </div>
            </div>

            <div className="route-details">
              <div className="detail-item">
                <span className="detail-label">Distance:</span>
                <span className="detail-value">{route.distance} nm</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Block Time:</span>
                <span className="detail-value">{route.estimatedBlockTime} min</span>
              </div>
            </div>

            <div className="route-explanation">
              <p>{route.explanation}</p>
            </div>

            <div className="route-actions">
              <button
                className="action-button primary"
                onClick={() => onPlanInSimBrief(route)}
              >
                Plan in SimBrief
              </button>
              <button
                className="action-button secondary"
                onClick={() => onViewMap(route)}
              >
                View on Map
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
